/**
 * Provider del sistema de tours.
 *
 * El TourRunner (que importa useJoyride y gestiona el portal de joyride)
 * SOLO se monta cuando hay un tour activo. Cuando activeId es null, nada
 * del runtime de joyride existe.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useJoyride, STATUS, type EventData } from 'react-joyride'
import { TOUR_REGISTRY } from './registry'
import type { TourDefinition, TourId } from './types'
import { isTourSeen, markTourSeen, resetTourSeen } from './storage'
import { useAuthStore } from '@/store/authStore'

interface TourContextValue {
  startTour: (id: TourId) => void
  stopTour: () => void
  availableTours: TourDefinition[]
  activeId: TourId | null
}

const TourContext = createContext<TourContextValue | null>(null)

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error('useTour debe usarse dentro de <TourProvider>')
  return ctx
}

const JOYRIDE_STYLES = {
  tooltip: { borderRadius: 12, padding: 16, fontSize: 14 },
  tooltipTitle: { fontSize: 15, fontWeight: 600, marginBottom: 6 },
  tooltipContent: { fontSize: 13, lineHeight: 1.55, padding: '4px 0' },
  buttonNext: { borderRadius: 8, padding: '8px 14px', fontSize: 13 },
  buttonBack: { color: 'rgb(var(--content-muted) / 1)', marginRight: 8, fontSize: 13 },
  buttonSkip: { color: 'rgb(var(--content-muted) / 1)', fontSize: 13 },
}

const JOYRIDE_LOCALE = {
  back: 'Atrás',
  close: 'Cerrar',
  last: 'Finalizar',
  next: 'Siguiente',
  // Con `showProgress` el botón usa ESTA clave y no `next`; sin ella salía el
  // default de react-joyride en inglés, «Next (1 of 4)» (visto el 2026-10-05).
  nextWithProgress: 'Siguiente ({current} de {total})',
  open: 'Abrir',
  skip: 'Saltar',
}

function TourRunner({
  tour,
  onEnd,
}: {
  tour: TourDefinition
  onEnd: () => void
}) {
  const handleEvent = useCallback((data: EventData) => {
    if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
      onEnd()
    }
  }, [onEnd])

  const { Tour } = useJoyride({
    steps: tour.steps,
    run: true,
    continuous: true,
    locale: JOYRIDE_LOCALE,
    styles: JOYRIDE_STYLES,
    onEvent: handleEvent,
    options: {
      showProgress: true,
      skipBeacon: true,
      closeButtonAction: 'skip',
      overlayClickAction: false,
      buttons: ['back', 'close', 'primary'],
      zIndex: 10000,
      primaryColor: '#327453',
      overlayColor: 'rgba(0, 0, 0, 0.5)',
    },
  })

  return <>{Tour}</>
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [activeId, setActiveId] = useState<TourId | null>(null)
  const [runToken, setRunToken] = useState(0)

  const userId = user?.id ?? ''
  const activeTour = activeId ? TOUR_REGISTRY[activeId] : null

  const startTour = useCallback((id: TourId) => {
    if (userId) resetTourSeen(userId, id)
    const def = TOUR_REGISTRY[id]
    const route = def?.autoTriggerOn?.[0]
    const onRoute = !route
      || location.pathname === route
      || location.pathname.startsWith(route + '/')

    if (!onRoute && route) {
      // Navega primero, después el auto-trigger en useEffect lo dispara cuando
      // el user llegue a la ruta y los selectores `data-tour` ya estén en DOM.
      navigate(route)
    } else {
      setActiveId(id)
      setRunToken(t => t + 1)
    }
  }, [userId, location.pathname, navigate])

  const stopTour = useCallback(() => {
    setActiveId(null)
  }, [])

  const handleEnd = useCallback(() => {
    if (activeId && userId) markTourSeen(userId, activeId)
    setActiveId(null)
  }, [activeId, userId])

  // Auto-trigger en ruta
  useEffect(() => {
    if (!isAuthenticated || !userId || activeId) return
    for (const def of Object.values(TOUR_REGISTRY)) {
      if (!def.autoTriggerOn || def.autoTriggerOn.length === 0) continue
      const matches = def.autoTriggerOn.some(
        p => location.pathname === p || location.pathname.startsWith(p + '/'),
      )
      if (matches && !isTourSeen(userId, def.id)) {
        const t = setTimeout(() => {
          setActiveId(def.id)
          setRunToken(tk => tk + 1)
        }, 500)
        return () => clearTimeout(t)
      }
    }
  }, [location.pathname, isAuthenticated, userId, activeId])

  const availableTours = useMemo(() => Object.values(TOUR_REGISTRY), [])

  return (
    <TourContext.Provider value={{ startTour, stopTour, availableTours, activeId }}>
      {children}
      {activeTour && (
        <TourRunner key={runToken} tour={activeTour} onEnd={handleEnd} />
      )}
    </TourContext.Provider>
  )
}
